"use client";

import { Card } from "@/components/ui/card";
import { metalText } from "./chrome";

export interface ChatToolCall {
  tool: string;
  args: string;
}

export interface ChatTurn {
  question: string;
  toolCalls: ChatToolCall[];
  /** Answer paragraphs. Wrap highlights in <Hi> for silver emphasis. */
  answer: React.ReactNode;
}

/** Silver-emphasised value inside an AI answer. */
export function Hi({ children }: { children: React.ReactNode }) {
  return <span className="text-[#e8eae9]">{children}</span>;
}

/**
 * Claude-style conversation mock following the QW Intelligence chat grammar:
 * user prompts are tinted metal bubbles with a tucked corner, the AI answer is
 * bubble-less with a single leading sparkle.
 */
export function ChatDemo({ turns }: { turns: ChatTurn[] }) {
  return (
    <Card className="mx-auto mt-10 max-w-3xl border-[#1f2020] bg-[#101111] p-6 sm:p-8">
      <div className="flex flex-col gap-7">
        {turns.map((turn) => (
          <div key={turn.question} className="flex flex-col gap-4">
            <div className="flex justify-end">
              <span
                className="max-w-[85%] rounded-xl rounded-br-[4px] border border-[#2b2d2c] px-3.5 py-2.5 text-sm leading-relaxed text-[#e8eae9]"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(232,234,233,.09), rgba(127,231,200,.05))",
                }}
              >
                {turn.question}
              </span>
            </div>

            {turn.toolCalls.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {turn.toolCalls.map((call) => (
                  <span
                    key={`${call.tool}-${call.args}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#2b2d2c] px-3 py-1 font-mono text-[11px] text-[#8d918f]"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/80" aria-hidden="true" />
                    {call.tool}
                    <span className="text-[#5b5b5b]">{call.args}</span>
                  </span>
                ))}
              </div>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <span className="text-xs" style={metalText}>
                &#10022; Claude &middot; via QuantWheel MCP
              </span>
              <div className="text-sm leading-relaxed text-[#c6c8c7]">{turn.answer}</div>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-7 border-t border-[#1c1d1d] pt-4 text-center text-[11px] text-white/35">
        Illustrative conversation. Answers are generated from live QuantWheel data at ask time.
      </p>
    </Card>
  );
}
